import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { discussions } from "@/mocks/discussions";
import { topics } from "@/mocks/topics";
import usePageSeo from "@/hooks/usePageSeo";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import DiscussionCard from "@/components/feature/DiscussionCard";
import { formatCount } from "@/components/feature/TopicCard";

const ORIGIN = "https://houaiw.cn";

const stanceOptions = ["全部", "支持", "质疑", "中立"];
const sortOptions = [
  { id: "hot", label: "最热" },
  { id: "new", label: "最新" },
];

export default function DiscussionPage() {
  const [stance, setStance] = useState("全部");
  const [sort, setSort] = useState("hot");

  const list = useMemo(() => {
    const base = discussions.filter((item) => stance === "全部" || item.stance === stance);
    return sort === "hot"
      ? [...base].sort((a, b) => b.likes - a.likes)
      : [...base].reverse();
  }, [stance, sort]);

  const hotTopics = useMemo(() => [...topics].sort((a, b) => b.comments - a.comments).slice(0, 5), []);

  usePageSeo({
    title: "瓜田吃瓜讨论区 - 热门话题观点交锋与网友评论汇总",
    description:
      "瓜田吃瓜讨论区汇集网友对热门话题的真实观点，支持、质疑、中立三种立场同屏交锋，评论与回复实时汇总，一键围观不同立场的思考方式。",
    keywords: "瓜田吃瓜讨论区,瓜田吃瓜观点交锋,网友评论,话题讨论",
    path: "/discussion",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "瓜田吃瓜讨论区",
        url: `${ORIGIN}/discussion`,
        inLanguage: "zh-CN",
        description:
          "集中展示网友对热门话题的观点表达，包含支持、质疑与中立三种立场，并可按热度与时间排序。",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: `${ORIGIN}/` },
          { "@type": "ListItem", position: 2, name: "讨论区", item: `${ORIGIN}/discussion` },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "讨论区热门观点",
        itemListElement: list.slice(0, 8).map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: `${item.author}：${item.content.slice(0, 40)}`,
          url: `${ORIGIN}/topic/${item.topicId}`,
        })),
      },
    ],
  });

  return (
    <div className="w-full">
      <section className="w-full bg-background-200/50 border-b border-background-200/70">
        <div className="w-full px-4 md:px-8 py-8 md:py-12">
          <Breadcrumbs items={[{ label: "讨论区" }]} />
          <div className="mt-6 max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-accent-700 mb-3">
              <span className="w-6 h-px bg-accent-400" />
              Discussion
            </span>
            <h1 className="text-3xl md:text-5xl font-bold text-foreground-950 leading-tight">
              讨论区 · 观点交锋
            </h1>
            <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600">
              同一个话题，本来就允许有不同的判断。这里保留支持、质疑与中立的三种声音，你可以按立场筛选，也可以只看讨论最激烈的那一批。
            </p>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
          <div className="lg:col-span-2">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="inline-flex px-1 py-1 rounded-full bg-background-100 border border-background-200/80">
                {stanceOptions.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setStance(option)}
                    className={`px-3.5 md:px-4 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors duration-200 ${
                      stance === option
                        ? "bg-background-50 text-primary-700 font-medium"
                        : "text-foreground-600 hover:text-primary-700"
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>

              <div className="inline-flex px-1 py-1 rounded-full bg-background-100 border border-background-200/80 self-start">
                {sortOptions.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => setSort(option.id)}
                    className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors duration-200 ${
                      sort === option.id
                        ? "bg-background-50 text-primary-700 font-medium"
                        : "text-foreground-600 hover:text-primary-700"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-6 flex flex-col gap-4">
              {list.length > 0 ? (
                list.map((item) => <DiscussionCard key={item.id} item={item} />)
              ) : (
                <div className="rounded-lg border border-background-200/80 bg-background-50 p-10 text-center">
                  <i className="ri-chat-off-line text-3xl text-foreground-400" />
                  <p className="mt-3 text-sm text-foreground-600">这个立场暂时还没有观点，换个筛选试试。</p>
                </div>
              )}
            </div>
          </div>

          <aside className="flex flex-col gap-6">
            <div className="rounded-lg bg-accent-50 border border-accent-200/70 p-5">
              <h3 className="text-base font-bold text-accent-950">讨论区基本共识</h3>
              <ul className="mt-4 flex flex-col gap-3 text-sm text-accent-900/85">
                <li className="flex items-start gap-2.5">
                  <i className="ri-check-line text-accent-600 mt-0.5" />
                  可以强烈质疑观点，但不要攻击发言的人。
                </li>
                <li className="flex items-start gap-2.5">
                  <i className="ri-check-line text-accent-600 mt-0.5" />
                  涉及具体个人时，以已公开信息为依据。
                </li>
                <li className="flex items-start gap-2.5">
                  <i className="ri-check-line text-accent-600 mt-0.5" />
                  承认自己可能判断错，是讨论的一部分。
                </li>
              </ul>
            </div>

            <div className="rounded-lg bg-background-200/50 border border-background-200/70 p-5">
              <h3 className="text-base font-bold text-foreground-950">讨论最激烈的话题</h3>
              <div className="mt-4 flex flex-col gap-1">
                {hotTopics.map((topic, index) => (
                  <Link
                    key={topic.id}
                    to={`/topic/${topic.id}`}
                    className="group flex items-start gap-3 py-3 border-b border-background-200/70 last:border-b-0"
                  >
                    <span className="shrink-0 w-6 h-6 flex items-center justify-center rounded-md bg-background-50 text-xs text-foreground-500">
                      {index + 1}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm text-foreground-800 group-hover:text-primary-700 transition-colors leading-snug">
                        {topic.title}
                      </span>
                      <span className="mt-1 inline-flex items-center gap-1 text-xs text-foreground-500">
                        <i className="ri-chat-3-line" />
                        {formatCount(topic.comments)} 条讨论
                      </span>
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}